import React, {useState} from 'react';

import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import * as BenchlingUtil from 'BenchlingUtil';
import clampEntryName from 'notebook/entry-templates/clampEntryName';
import TemplateCollectionDAO from 'notebook/entry-templates/collections/TemplateCollectionDAO';
import TemplateCollectionSelector from 'notebook/entry-templates/collections/TemplateCollectionSelector';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import Row from 'ui-library/bs/Row';
import TextField from 'ui-library/inputs/TextField';
import Modal from 'ui-library/modals/Modal';
import {withLoadingScreen} from 'util/Promises';
import {BasicEvent} from 'util/Types';

import {TemplateCollectionGraphQL} from '../../../graphql-fragments/notebook/templates/__generated/TemplateCollectionGraphQL';
import {createEmptyAnalysisTemplate} from './AnalysisTemplateActions';

export interface CreateEmptyAnalysisTemplateModalProps {
  onRequestClose: () => void;
  initialTemplateCollection: TemplateCollectionGraphQL;
}

export default function CreateEmptyAnalysisTemplateModal(
  props: CreateEmptyAnalysisTemplateModalProps
): JSX.Element {
  const [name, setName] = useState('');
  const [templateCollection, setTemplateCollection] = useState(props.initialTemplateCollection);

  const trimmedName = name.trim();
  const isNameBlank = name == null || trimmedName === '';

  function manageCollections(e: BasicEvent): void {
    const url = TemplateCollectionDAO.collectionsURL();
    BenchlingUtil.navigate(e, url);
    props.onRequestClose();
  }

  function isSubmitEnabled(): boolean {
    const hasTemplateCollection = templateCollection != null;
    return hasTemplateCollection && !isNameBlank;
  }

  return (
    <Modal
      title="Create analysis template"
      onRequestClose={props.onRequestClose}
      onRequestSubmit={async () => {
        await withLoadingScreen(
          createEmptyAnalysisTemplate(trimmedName, templateCollection.id),
          `Creating new analysis template...`
        );
        props.onRequestClose();
      }}
      buttons={[
        {
          text: 'Create',
          submit: true,
          disabled: !isSubmitEnabled(),
          tooltipTitle: isNameBlank ? 'Analysis template names must be non-empty.' : null,
        },
        {text: 'Manage Collections', onClick: manageCollections, asLink: true, pullLeft: true},
      ]}
      dataTestAttrs={{component: 'CreateAnalysisTemplateModal', element: 'modal'}}
      analyticsModule={ANALYTICS_MODULE.ANALYSIS_TOOL}
      analyticsModalType="create_empty_analysis_template"
    >
      <Row>
        <Col sm={7}>
          <FormGroup dataTestAttrs={{component: 'CreateAnalysisTemplateModal', element: 'name'}}>
            <TextField
              label="Name"
              placeholder="Enter a name for this analysis"
              autoFocus={true}
              value={name}
              onChange={(e: {target: {value: string}}) => {
                setName(clampEntryName(e.target.value));
              }}
              dataTestAttrs={{component: 'CreateAnalysisModal', element: 'nameTextField'}}
            />
          </FormGroup>
        </Col>
        <Col sm={5}>
          <Label>Template Collection</Label>
          <TemplateCollectionSelector
            searchable={true}
            value={templateCollection}
            onChange={(collection) => {
              setTemplateCollection(collection);
            }}
          />
        </Col>
      </Row>
    </Modal>
  );
}
